import { useEffect, useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import type { PositionsWidgetRow } from "./PositionsWidget.types";

export interface ClosePositionConfirmOptions {
  cancelOrders: boolean;
  closeRatioPct?: number;
  partialQty?: number;
}

export interface UseClosePositionDialogControllerParams {
  open: boolean;
  positions: PositionsWidgetRow[];
  /** Set only for a bulk close, where one ratio applies to every selection. */
  closeModalRatioPct?: number;
  onConfirmClose: (options: ClosePositionConfirmOptions) => void;
}

export interface ClosePositionDialogController {
  /** Set only when the selection is exactly one position. */
  single: PositionsWidgetRow | undefined;
  maxQty: number;
  showBulkRatio: boolean;
  partialQty: number;
  bulkRatio: number;
  canSubmit: boolean;
  onPartialQtyChange: (value: number) => void;
  onBulkRatioChange: (value: number) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}

interface ClosePositionFormValues {
  partialQty: number;
  bulkRatio: number;
}

/** `partialQty` only validates when the single-position slider is shown. */
const createClosePositionSchema = ({
  isPartialQtyOperative,
  maxQty,
}: {
  isPartialQtyOperative: boolean;
  maxQty: number;
}) =>
  z
    .object({
      partialQty: z.number(),
      bulkRatio: z.number().int().min(1).max(100),
    })
    .superRefine(({ partialQty }, ctx) => {
      if (!isPartialQtyOperative) {
        return;
      }
      if (partialQty <= 0) {
        ctx.addIssue({
          code: "custom",
          message: "Enter a quantity greater than zero.",
          path: ["partialQty"],
        });
        return;
      }
      if (partialQty > maxQty) {
        ctx.addIssue({
          code: "custom",
          message: "Quantity cannot exceed the position size.",
          path: ["partialQty"],
        });
      }
    });

export const useClosePositionDialogController = ({
  open,
  positions,
  closeModalRatioPct,
  onConfirmClose,
}: UseClosePositionDialogControllerParams): ClosePositionDialogController => {
  const single = positions.length === 1 ? positions[0] : undefined;
  const maxQty = single?.quantityValue ?? 0;
  const showBulkRatio = positions.length > 1 && closeModalRatioPct != null;
  const isPartialQtyOperative = Boolean(single) && !showBulkRatio;

  const schema = useMemo(
    () => createClosePositionSchema({ isPartialQtyOperative, maxQty }),
    [isPartialQtyOperative, maxQty],
  );

  const { handleSubmit, reset, setValue, watch } =
    useForm<ClosePositionFormValues>({
      resolver: zodResolver(schema),
      defaultValues: {
        partialQty: maxQty,
        bulkRatio: closeModalRatioPct ?? 100,
      },
    });

  // biome-ignore lint/correctness/useExhaustiveDependencies: open and positions are triggers — reopening the dialog or changing the selection re-seeds the sliders. The body writes rather than reads them.
  useEffect(() => {
    reset({ partialQty: maxQty, bulkRatio: closeModalRatioPct ?? 100 });
  }, [closeModalRatioPct, maxQty, open, positions, reset]);

  const partialQty = watch("partialQty");
  const bulkRatio = watch("bulkRatio");

  return {
    single,
    maxQty,
    showBulkRatio,
    partialQty,
    bulkRatio,
    // Dim early; the resolver still blocks submit.
    canSubmit:
      positions.length > 0 && !(isPartialQtyOperative && partialQty <= 0),
    onPartialQtyChange: (value) => setValue("partialQty", value),
    onBulkRatioChange: (value) => setValue("bulkRatio", value),
    onSubmit: handleSubmit((values) => {
      onConfirmClose({
        cancelOrders: false,
        closeRatioPct: showBulkRatio ? values.bulkRatio : undefined,
        partialQty: isPartialQtyOperative ? values.partialQty : undefined,
      });
    }),
  };
};
